import React, { useEffect } from "react";
import { selectSidebar } from "actions/layout/layout";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { WithLoader } from "components";
import {
    dashboardSearchData,
    setColumnPerPage,
    setDataToRender,
    setSearchKeys,
    setOrder
} from "actions/dashboard/dashboard";
import { useNavigate } from "react-router-dom";
import { getStatus, multiProductStatus } from "utils/utils";
import { useTranslations } from "hooks/UseTranslations";
import DataGridTable from "components/DataGridTable/DataGridTable";
import PageHeader from "components/PageHeader/PageHeader";
import Acordion from "components/Acordion/Acordion";
import MobileView from "./MyOrdersMobileView";
import DataGridFilters from "components/DataGridTable/DataGridFilters/DataGridFilters";
import useCheckMobileScreen from "hooks/UseCheckMobileScreen/UseCheckMobileScreen";
import relativeTime from "dayjs/plugin/relativeTime";
import dayjs from "dayjs";
import styles from "components/DataGridTable/DataGridTable.module.scss";

function MyOrders() {
    dayjs.extend(relativeTime);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const checkIfMobile = useCheckMobileScreen();
    const { translations } = useTranslations();
    const { action, allOrders, filteredData, columndPerPage, order } = useAppSelector(
        (state) => state.dashboardReducer
    );

    const tableColumns = [
        {
            field: "ta_id",
            headerName: `${translations("title.id")}`,
            minWidth: 100,
            flex: 0.1,
            sortable: false,
            renderCell: (params: any) => <p className={styles.id}>{params.value + "/" + params.row.transaction_id}</p>
        },
        {
            field: "booking_date",
            headerName: `${translations("title.order_date")}`,
            minWidth: 80,
            flex: 0.25,
            sortable: false,
            renderCell: (params: any) => <p>{dayjs(params.value).format("DD.MM.YYYY")}</p>
        },
        {
            field: "product_name",
            headerName: `${translations("title.product")}`,
            flex: 0.9,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => {
                if (params.row.items.length > 1) {
                    return (
                        <Acordion
                            style={{ boxShadow: "none !important" }}
                            styleTitle={{ fontSize: "13px" }}
                            title={params.value}
                            description={products(params.row.items)}
                        />
                    );
                }
                return <b style={{ padding: "0px 16px" }}>{params.row.product_name}</b>;
            }
        },
        {
            field: "merchant_name",
            headerName: `${translations("title.supplier")}`,
            sortable: false,
            minWidth: 160,
            flex: 0.3
        },
        {
            field: "status",
            headerName: `${translations("title.status")}`,
            flex: 0.25,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => {
                let status_record_states = allOrders
                    .filter((el) => el.product_order_id === params.row.product_order_id)
                    .map((el) => el.item_record_state);

                const status = params.row.appointment_status === "M" ? "status.missed" :
                    status_record_states.length > 1
                        ? multiProductStatus(status_record_states)
                        : getStatus(params.row.record_state);

                let color;
                switch (status) {
                    case "status.missed":
                        color = "rgb(192 32 32)";
                        break;
                    case "status.closed":
                        color = "#454545";
                        break;
                    case "status.open":
                        color = "#20C073";
                        break;
                    case "status.canceled":
                        color = "#E42011";
                        break;
                    default:
                        color = "white";
                        break;
                }

                return <span style={{ color: color }}>{" • " + translations(status)}</span>;
            }
        },
        {
            field: "amount",
            headerName: `${translations("label.total")}`,
            flex: 0.1,
            minWidth: 100,
            sortable: false,
            valueGetter: getTotal
        },
        {
            field: "action",
            headerName: "",
            headerAlign: "right",
            align: "right",
            sortable: false,
            minWidth: 150,
            flex: 0.3,
            renderCell: (params) => {
                return (
                    <button
                        onClick={() => navigate(`/web/my-orders/view-order/${params.row.product_order_id}`)}
                        style={{
                            border: "1px solid #DFE3EC",
                            borderRadius: 10,
                            padding: "6px 12px"
                        }}>
                        {translations("button.view_order")}
                    </button>
                );
            }
        }
    ];

    function getTotal(params) {
        const hasDecimalPoint = params.row.amount.toString().includes(".");
        return hasDecimalPoint ? `€ ${params.row.amount}0 ` : `€ ${params.row.amount}.00 `;
    }

    const products = (items: []) => {
        let updateItems = [...items];
        updateItems.shift();
        return (
            <div className={styles.accordion}>
                {updateItems.map((el, i) => (
                    <p key={i}>{el}</p>
                ))}
            </div>
        );
    };

    const handleSearch = (e) => {
        dispatch(dashboardSearchData(e.target.value));
    };

    const handleShow = (value: number) => {
        dispatch(setColumnPerPage(value));
    };

    const handleOrder = (value: string) => {
        dispatch(setOrder(value));
    };

    let obj = {};
    const updatedAllOrders = allOrders.reduce((result, el) => {
        const order_id = el.product_order_id;
        if (!obj[order_id]) {
            obj[order_id] = {
                ...el,
                items: []
            };
            result.push(obj[order_id]);
        }
        obj[order_id].items.push(el.product_name);
        return result;
    }, []);

    useEffect(() => {
        document.title = `Ecocare - My Orders`;
        dispatch(selectSidebar("my-orders"));
        dispatch(setSearchKeys(["product_name", "items"]));
        dispatch(setDataToRender(updatedAllOrders));

        return function cleanup() {
            dispatch(setSearchKeys([]));
            dispatch(setDataToRender([]));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, allOrders]);

    return (
        <div>
            <PageHeader
                pageTitle={translations("orders.your_orders")}
                // pageSubTitle={
                //   "From Your account dashboard you can view recent appointments, orders, and health profile  also you can manage your account details and change your password"
                // }
                icon={"icon-myOrders active"}
                hasBackButton={false}
            />
            <WithLoader isLoading={action.isLoading}>
                {checkIfMobile ? (
                    <div>
                        <DataGridFilters
                            placeholder={translations("placeholder.search_by_product_name")}
                            tableName=""
                            handleSearch={handleSearch}
                            filterOptions={["show", "order"]}
                            setPageColumn={handleShow}
                            columnsPerPage={columndPerPage}
                            setOrder={handleOrder}
                            order={order}
                        />
                        <MobileView
                            filteredData={filteredData}
                            pageSize={columndPerPage}
                            allOrders={allOrders}
                            emptyStateMsg={translations("you_dont_have_any_orders_yet")}
                        />
                    </div>
                ) : (
                    <div className={styles.border}>
                        <DataGridTable
                            getRowId={(row: any) => row.uid}
                            tableColumns={tableColumns}
                            data={filteredData}
                            tableName=""
                            handleSearch={handleSearch}
                            filterOptions={["show", "order"]}
                            setPageColumn={handleShow}
                            columnsPerPage={columndPerPage}
                            setOrder={handleOrder}
                            order={order}
                            pageSize={columndPerPage}
                            placeholder={translations("placeholder.search_by_product_name")}
                            emptyStateMsg={translations("you_dont_have_any_orders_yet")}
                        />
                    </div>
                )}
            </WithLoader>
        </div>
    );
}

export default MyOrders;
